import {
  EInvoiceStatus,
  FiscalDocumentStatus,
  PaymentMethod,
  SalesDocumentStatus,
  SalesDocumentType,
  RecurringFrequency,
  ReconciliationMode,
  BankStatementLineStatus,
} from "@finora/types";

/**
 * @finora/config
 *
 * Konstante koje bi inače bile hardkodovane po komponentama -
 * nazivi statusa, boje, difolt valuta. Frontend uvek prikazuje
 * status preko FISCAL_DOCUMENT_STATUS_LABELS, nikad kroz sirovi
 * enum string.
 */

export const DEFAULT_CURRENCY = "RSD";

export const FISCAL_DOCUMENT_STATUS_LABELS: Record<
  FiscalDocumentStatus,
  string
> = {
  [FiscalDocumentStatus.DRAFT]: "Nacrt",
  [FiscalDocumentStatus.PAYMENT_PENDING]: "Čeka naplatu",
  [FiscalDocumentStatus.READY_FOR_FISCALIZATION]: "Spremno za fiskalizaciju",
  [FiscalDocumentStatus.FISCALIZATION_PENDING]: "Fiskalizacija u toku",
  [FiscalDocumentStatus.FISCALIZED]: "Fiskalizovano",
  [FiscalDocumentStatus.FISCALIZATION_FAILED]: "Fiskalizacija neuspešna",
  [FiscalDocumentStatus.CANCELLED]: "Otkazano",
  [FiscalDocumentStatus.REFUNDED]: "Refundirano",
};

export type StatusTone = "neutral" | "info" | "success" | "warning" | "error";

export const FISCAL_DOCUMENT_STATUS_TONE: Record<
  FiscalDocumentStatus,
  StatusTone
> = {
  [FiscalDocumentStatus.DRAFT]: "neutral",
  [FiscalDocumentStatus.PAYMENT_PENDING]: "warning",
  [FiscalDocumentStatus.READY_FOR_FISCALIZATION]: "info",
  [FiscalDocumentStatus.FISCALIZATION_PENDING]: "info",
  [FiscalDocumentStatus.FISCALIZED]: "success",
  [FiscalDocumentStatus.FISCALIZATION_FAILED]: "error",
  [FiscalDocumentStatus.CANCELLED]: "neutral",
  [FiscalDocumentStatus.REFUNDED]: "warning",
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  [PaymentMethod.CASH]: "Gotovina",
  [PaymentMethod.CARD]: "Platna kartica",
  [PaymentMethod.BANK_TRANSFER]: "Prenos na račun",
  [PaymentMethod.VOUCHER]: "Vaučer",
  [PaymentMethod.COMBINED]: "Kombinovano",
  [PaymentMethod.INSTANT_PAYMENT]: "IPS / instant plaćanje",
  [PaymentMethod.CHECK]: "Ček",
  [PaymentMethod.OTHER]: "Drugo bezgotovinsko",
};

export const EINVOICE_STATUS_LABELS: Record<EInvoiceStatus, string> = {
  [EInvoiceStatus.DRAFT]: "Nacrt",
  [EInvoiceStatus.SENT]: "Poslata",
  [EInvoiceStatus.PENDING_DECISION]: "Čeka odluku",
  [EInvoiceStatus.APPROVED]: "Odobrena",
  [EInvoiceStatus.REJECTED]: "Odbijena",
  [EInvoiceStatus.CANCELLED]: "Otkazana",
};

export const EINVOICE_STATUS_TONE: Record<EInvoiceStatus, StatusTone> = {
  [EInvoiceStatus.DRAFT]: "neutral",
  [EInvoiceStatus.SENT]: "info",
  [EInvoiceStatus.PENDING_DECISION]: "warning",
  [EInvoiceStatus.APPROVED]: "success",
  [EInvoiceStatus.REJECTED]: "error",
  [EInvoiceStatus.CANCELLED]: "neutral",
};

// --- Faza 2: Ponude/Fakture ------------------------------------------------

export const SALES_DOCUMENT_TYPE_LABELS: Record<SalesDocumentType, string> = {
  [SalesDocumentType.QUOTE]: "Ponuda",
  [SalesDocumentType.PROFORMA_INVOICE]: "Predračun",
  [SalesDocumentType.INVOICE]: "Faktura",
  [SalesDocumentType.ADVANCE_INVOICE]: "Avansna faktura",
  [SalesDocumentType.FINAL_INVOICE]: "Konačna faktura",
  [SalesDocumentType.CREDIT_NOTE]: "Knjižno odobrenje",
  [SalesDocumentType.DEBIT_NOTE]: "Knjižno zaduženje",
};

export const SALES_DOCUMENT_STATUS_LABELS: Record<
  SalesDocumentStatus,
  string
> = {
  [SalesDocumentStatus.DRAFT]: "Nacrt",
  [SalesDocumentStatus.APPROVED]: "Odobreno",
  [SalesDocumentStatus.SENT]: "Poslato",
  [SalesDocumentStatus.VIEWED]: "Pregledano",
  [SalesDocumentStatus.ACCEPTED]: "Prihvaćeno",
  [SalesDocumentStatus.REJECTED]: "Odbijeno",
  [SalesDocumentStatus.EXPIRED]: "Isteklo",
  [SalesDocumentStatus.CONVERTED]: "Pretvoreno u fakturu",
  [SalesDocumentStatus.PARTIALLY_PAID]: "Delimično plaćeno",
  [SalesDocumentStatus.PAID]: "Plaćeno",
  [SalesDocumentStatus.OVERDUE]: "Dospelo",
  [SalesDocumentStatus.CANCELLED]: "Storniran",
};

export const SALES_DOCUMENT_STATUS_TONE: Record<
  SalesDocumentStatus,
  StatusTone
> = {
  [SalesDocumentStatus.DRAFT]: "neutral",
  [SalesDocumentStatus.APPROVED]: "info",
  [SalesDocumentStatus.SENT]: "info",
  [SalesDocumentStatus.VIEWED]: "info",
  [SalesDocumentStatus.ACCEPTED]: "success",
  [SalesDocumentStatus.REJECTED]: "error",
  [SalesDocumentStatus.EXPIRED]: "neutral",
  [SalesDocumentStatus.CONVERTED]: "success",
  [SalesDocumentStatus.PARTIALLY_PAID]: "warning",
  [SalesDocumentStatus.PAID]: "success",
  [SalesDocumentStatus.OVERDUE]: "error",
  [SalesDocumentStatus.CANCELLED]: "neutral",
};

/** Format novčanog decimalnog stringa za prikaz, npr. "1234.5" -> "1.234,50 RSD". */
export function formatMoney(
  value: string | number,
  currency: string = DEFAULT_CURRENCY,
): string {
  const numeric = typeof value === "string" ? Number(value) : value;
  const formatted = new Intl.NumberFormat("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
  return `${formatted} ${currency}`;
}

// --- Ponavljajuće fakture ---------------------------------------------------

export const RECURRING_FREQUENCY_LABELS: Record<RecurringFrequency, string> = {
  [RecurringFrequency.DAILY]: "Dnevno",
  [RecurringFrequency.WEEKLY]: "Nedeljno",
  [RecurringFrequency.MONTHLY]: "Mesečno",
  [RecurringFrequency.YEARLY]: "Godišnje",
};

// --- Bankovni izvodi ---------------------------------------------------------

export const RECONCILIATION_MODE_LABELS: Record<ReconciliationMode, string> = {
  [ReconciliationMode.AUTO]: "Automatski",
  [ReconciliationMode.CONFIRM]: "Potvrda knjiženja",
  [ReconciliationMode.MANUAL]: "Ručno",
};

export const BANK_STATEMENT_LINE_STATUS_LABELS: Record<BankStatementLineStatus, string> = {
  [BankStatementLineStatus.UNMATCHED]: "Nije pronađeno",
  [BankStatementLineStatus.SUGGESTED]: "Predloženo",
  [BankStatementLineStatus.BOOKED]: "Proknjiženo",
  [BankStatementLineStatus.IGNORED]: "Ignorisano",
};

export const BANK_STATEMENT_LINE_STATUS_TONE: Record<BankStatementLineStatus, StatusTone> = {
  [BankStatementLineStatus.UNMATCHED]: "neutral",
  [BankStatementLineStatus.SUGGESTED]: "warning",
  [BankStatementLineStatus.BOOKED]: "success",
  [BankStatementLineStatus.IGNORED]: "neutral",
};
